import { Component, OnInit, signal, inject, Inject, PLATFORM_ID, DestroyRef } from '@angular/core';
import { CommonModule, CurrencyPipe, isPlatformBrowser } from '@angular/common';
import { SkeletonModule } from 'primeng/skeleton';
import { ApiService } from '../../../../services/api.service';
import { interval, takeWhile, tap, finalize, scan, Subscription, EMPTY, catchError } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-main-goodmeter',
  standalone: true,
  imports: [CommonModule, CurrencyPipe, SkeletonModule],
  templateUrl: './main-goodmeter.component.html',
  styleUrls: ['./main-goodmeter.component.scss']
})
export class MainGoodmeterComponent implements OnInit {
  amount = signal<number | null>(null);
  private readonly apiService = inject(ApiService);
  private animSub?: Subscription;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private destroyRef: DestroyRef
  ) { }

  ngOnInit(): void {
    // Only run in the browser; defer to next macrotask to avoid hydration/HMR races
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => this.startGoodMeter(), 0);
    }
  }

  private startGoodMeter() {
    this.apiService.getGoodMeterData()
      .pipe(
        // optional: swallow aborts/timeouts in dev to avoid overlay noise
        catchError(() => EMPTY),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(result => {
        let { raised, startingAmount, tick } = result;

        //test values 
        //raised = 518769988;
        //startingAmount = 504841114;
        //tick = 1500; // interval between steps (ms)

        if (startingAmount < raised) {
          if ((tick <= 0) || (tick > 2000)) {
            tick = 1500;
          }
          this.amount.set(startingAmount);
          this.animateAmount(startingAmount, raised, tick, 1);
        } else {
          this.amount.set(raised);
        }
      });
  }

  /**
   * Animate from -> to, updating `this.amount` every `tickMs`.
   * @param from startingAmount
   * @param to raised
   * @param tickMs interval between steps (milliseconds)
   * @param stepPerTick optional fixed increment per tick; if omitted we derive one to finish in ~20 ticks
   */
  private animateAmount(from: number, to: number, tickMs: number, stepPerTick?: number) {
    if (tickMs <= 0 || from === to) {
      this.amount.set(to);
      return;
    }

    // cancel any existing animation (e.g., HMR re-renders)
    this.animSub?.unsubscribe();

    const diff = to - from;
    const dir = Math.sign(diff); // +1 up, -1 down
    const step = stepPerTick != null && stepPerTick > 0
      ? stepPerTick * dir
      : Math.max(1, Math.floor(Math.abs(diff) / 20)) * dir;

    this.animSub = interval(tickMs).pipe(
      scan(current => {
        const next = current + step;
        if ((dir > 0 && next >= to) || (dir < 0 && next <= to)) return to;
        return next;
      }, from),
      tap(v => this.amount.set(Math.round(v))),
      takeWhile(v => v !== to, true),
      finalize(() => this.amount.set(to)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }
}
